'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import type { NearbyShow } from '@bip/api-client';
import { IMG } from '../../../imgPrefix';
import { useCurrency } from '../../CurrencyProvider';
import { getStarValue, formatReviewCount } from '../../../_lib/shows/reviewFormat';
import './NearbyShowCard.css';

const FALLBACK_IMAGE = `${IMG}/image/no-image.png`;

function resolveShowImage(src: string | undefined): string {
  if (!src) return FALLBACK_IMAGE;
  return src.startsWith('http') ? src : `${IMG}${src}`;
}

function ddmmyyyyToISO(date: string | undefined | null): string {
  if (!date) return '';
  const match = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(String(date).trim());
  if (!match) return '';
  return `${match[3]}-${match[2].padStart(2, '0')}-${match[1].padStart(2, '0')}`;
}

function ShowImage({ src, alt }: { src?: string; alt: string }) {
  const [currentSrc, setCurrentSrc] = useState(resolveShowImage(src));

  useEffect(() => {
    setCurrentSrc(resolveShowImage(src));
  }, [src]);

  return (
    <img
      src={currentSrc}
      alt={alt}
      loading="lazy"
      onError={() => {
        if (currentSrc !== FALLBACK_IMAGE) setCurrentSrc(FALLBACK_IMAGE);
      }}
      className="rnsh-img"
    />
  );
}

function getGenreLabel(show: NearbyShow): string | null {
  const raw =
    (show as unknown as Record<string, unknown>).category as string | undefined
    ?? (show as unknown as Record<string, unknown>).genre as string | undefined
    ?? (show as unknown as Record<string, unknown>).type as string | undefined;
  if (!raw) return null;
  return String(raw).trim();
}

const GENRE_COLORS: Record<string, string> = {
  musical: '#9c3fbf',
  dance: '#c96cbf',
  play: '#7a60d4',
  comedy: '#2b8cbf',
  opera: '#bf6e3f',
  ballet: '#c96cbf',
  concert: '#3f7abf',
};

function genreBadgeColor(genre: string): string {
  return GENRE_COLORS[genre.toLowerCase()] ?? '#9c3fbf';
}

// Literal `/shows` — must match `app/shows/config.ts`'s `SHOWS_BASE_PATH`; can't import it
// directly, route isolation forbids this shared cross-domain card pulling in Shows-domain code.
const SHOWS_BASE_PATH = '/shows';

function bookHref(show: NearbyShow, bookingDate?: string): string {
  if (!show.slug) return SHOWS_BASE_PATH;
  const iso = bookingDate || ddmmyyyyToISO(show.dateOfEvent);
  return iso ? `${SHOWS_BASE_PATH}/${show.slug}?bookingDate=${iso}` : `${SHOWS_BASE_PATH}/${show.slug}`;
}

export function NearbyShowCard({
  show,
  bookingDate,
  referenceName,
}: {
  show: NearbyShow;
  bookingDate?: string;
  referenceName?: string;
}) {
  const { formatPrice } = useCurrency();
  const savingsPct =
    show.originalPrice > show.finalPrice
      ? Math.round(((show.originalPrice - show.finalPrice) / show.originalPrice) * 100)
      : 0;

  const rating = Number(show.rating ?? 0);
  const reviewCount = Number(show.reviewCount ?? 0);
  const hasRating = rating > 0;
  const hasReviews = Number.isInteger(reviewCount) && reviewCount > 0;

  const starValue = hasRating ? getStarValue(rating) : 0;
  const fullStars = Math.floor(starValue);
  const hasHalfStar = starValue % 1 !== 0;

  const genre = getGenreLabel(show);

  const location = [show.venueCity, show.venueState].filter(Boolean).join(', ');
  const distanceText =
    show.distanceMiles != null
      ? `, ${show.distanceMiles.toFixed(1)} miles ${referenceName ? ` from ${referenceName}` : ''}`
      : '';

  return (
    <div className="rnsh-card">
      <div className="rnsh-img-wrap">
        <ShowImage src={show.image ?? undefined} alt={show.name} />

        {genre && (
          <span className="rnsh-genre" style={{ background: genreBadgeColor(genre) }}>
            {genre}
          </span>
        )}
      </div>

      <div className="rnsh-body">
        <p className="rnsh-name">{show.name}</p>

        {(hasRating || hasReviews) && (
          <div className="rnsh-rating">
            {hasRating ? (
              <>
                <span className="rnsh-rating__num">{rating.toFixed(1)}</span>
                <span className="rnsh-rating__stars">
                  {Array.from({ length: 5 }, (_, i) => {
                    if (i < fullStars) return <i key={i} className="fas fa-star rnsh-star" />;
                    if (i === fullStars && hasHalfStar) return <i key={i} className="fas fa-star-half-alt rnsh-star" />;
                    return <i key={i} className="fal fa-star rnsh-star" />;
                  })}
                </span>
                {hasReviews && <span className="rnsh-rating__count">{formatReviewCount(reviewCount)}</span>}
              </>
            ) : (
              <span className="rnsh-rating__count">{formatReviewCount(reviewCount)}</span>
            )}
          </div>
        )}

        <p className="rnsh-location">
          <i className="text-lg fal fa-map-marker-alt rnsh-location__icon" />
          <span>{location}{distanceText}</span>
        </p>

        <p className="rnsh-price">
          {/* `formatPrice` always renders "<symbol> <amount>" with a
              space (shared convention across emails/checkout/payment) —
              this card is a deliberate exception, so the space is
              stripped here only, not in the shared helper. */}
          From {formatPrice(show.finalPrice, show.finalCurrency).replace(' ', '')}
        </p>

        {savingsPct > 0 && (
          <span className="rnsh-save">Save Up {savingsPct}%</span>
        )}

        <div className="rnsh-book-wrap">
          <Link
            href={bookHref(show, bookingDate)}
            className="rnsh-book"
            target="_blank"
            rel="noopener noreferrer"
          >
            Book now
          </Link>
        </div>
      </div>
    </div>
  );
}
